import React, { useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader/PageHeader';
import { v4 as uuid } from 'uuid';
import { CreateRumbleItem } from '../types/RumbleItem';
import { CreateParticipant } from '../types/Participant';
import { saveList } from '../logic/Helpers';
import RumbleList from '../types/RumbleList';
import Label from '../components/Label/Label';
import Input from '../components/Input/Input';
import TextArea from '../components/TextArea/TextArea';
import CustomImageUpload from '../components/CustomImageUpload/CustomImageUpload';
import ClearFileInputIcon from '../components/Icons/ClearFileInputIcon';
import Radio from '../components/Input/Radio';
import { useHistory } from 'react-router-dom';
import Button from '../components/Button/Button';
import SaveRumbleIcon from '../components/Icons/SaveRumbleIcon';
import clsx from 'clsx';
import BackArrowIcon from '../components/Icons/BackArrowIcon';
import PlusIcon from '../components/Icons/PlusIcon';
import Item from '../components/CreateList/Item';
import ExceededRumbleIcon from '../components/Icons/ExceededRumbleIcon';
import GetUnlimiterIcon from '../components/Icons/GetUnlimiterIcon';
import CustomTextFileUpload from '../components/CustomTextFileUpload/CustomTextFileUpload';
import AddParticipantIcon from '../components/Icons/AddParticipantIcon';
import DeleteIcon from '../components/Icons/DeleteIcon';
import { getUser, isUserReady } from '../redux/selectors';
import { connect } from 'react-redux';
import { setActiveList } from '../redux/actions';

function Create(props: {
  logOutModal: (params: boolean) => void;
  editModal: (params: boolean) => void;
  videoModal: (params: boolean) => void;
  upgradeModal: (params: boolean) => void;
  isEditOpen: boolean;
  isFullOpen: boolean;
  isMobile: boolean;
  duplicateRumbleID: number | null;
  resetDuplicateRumbleID: (params: boolean) => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState(null as File | null);
  const [imageURL, setImageURL] = useState(null as string | null);
  const [items, setItems] = useState([
    { imageFile: null, imageURL: '', name: '', description: '', key: uuid() },
  ] as CreateRumbleItem[]);
  const [participants, setParticipants] = useState<CreateParticipant[] | null>(
    null
  );
  const [type, setType] = useState('open');
  const [ready, setReady] = useState(false);
  const [canCreateLists, setCanCreateLists] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [nameError, setNameError] = useState(false);
  const [itemsNameError, setItemsNameError] = useState(false);
  const [emptyParticipantsError, setEmptyParticipantsError] = useState(false);
  const [emptyParticipantEmailError, setEmptyParticipantEmailError] =
    useState(false);
  const [countItemsError, setCountItemsError] = useState(false);
  const [addRumbleSteps, setAddRumblesSteps] = useState<1 | 2>(1);
  const [duplicateRumble, setDuplicateRumble] = useState<RumbleList>();
  const [isSaving, setIsSaving] = useState(false);
  const history = useHistory();

  async function selectList(listId: number) {
    const listResponse = await fetch(`/api/RumbleLists/${listId}`);
    const participantsResponse = await fetch(
      `/api/RumbleLists/${listId}/Participants`
    );
    let list = await listResponse.json();
    let participants = await participantsResponse.json();
    list.participants = participants.map((p: any) => ({
      emailAddress: p.emailAddress,
    }));
    setDuplicateRumble(list);
  }

  useEffect(() => {
    fetch('/api/Account/CanUserCreateLists')
      .then((resp) => resp.json())
      .then((json) => {
        setCanCreateLists(json);
        setReady(true);
      });
    if (props.duplicateRumbleID) {
      selectList(props.duplicateRumbleID).then(() =>
        props.resetDuplicateRumbleID(true)
      );
    }
  }, []);
  useEffect(() => {
    if (duplicateRumble) {
      setName(duplicateRumble.name);
      setType(duplicateRumble.type.toLowerCase());
      setDescription(duplicateRumble.description);
      setImageURL(duplicateRumble.imageURL);
      setItems(
        (duplicateRumble.items || []).map((item) => ({
          name: item.name,
          description: item.description,
          imageFile: null,
          imageURL: item.imageURL,
          key: uuid(),
        }))
      );
      setParticipants(
        (duplicateRumble.participants || []).map((participant) => ({
          ...participant,
          key: uuid(),
        }))
      );
    }
  }, [duplicateRumble]);

  function updateItem(item: CreateRumbleItem) {
    setItems((oldItems) =>
      oldItems.map((i) => (i.key === item.key ? item : i))
    );
  }

  function removeItem(key: string) {
    setItems((oldItems) => oldItems.filter((item) => item.key !== key));
  }

  function addNewItem() {
    setItems((oldItems) => [
        ...oldItems,
        { name: '', description: '', imageFile: null, imageURL: '', key: uuid() },
    ]);
  }

  function updateParticipant(participant: CreateParticipant) {
    setParticipants(
      (oldParticipants) =>
        oldParticipants &&
        oldParticipants.map((p) =>
          p.key === participant.key ? participant : p
        )
    );
  }

  function removeParticipant(key: string) {
    setParticipants(
      (oldParticipants) =>
        oldParticipants &&
        oldParticipants.filter((participant) => participant.key !== key)
    );
  }

  function addNewParticipant() {
    setParticipants((oldParticipants) =>
      oldParticipants
        ? [...oldParticipants, { emailAddress: '', key: uuid() }]
        : [{ emailAddress: '', key: uuid() }]
    );
  }

  function readParticipantList(inputFile: File) {
    let reader = new FileReader();
    reader.onloadend = function (event) {
      if (event.target?.readyState === FileReader.DONE) {
        let fileData = event.target.result as string;
        let emails = fileData.split('\n').map((line) => {
          return {
            emailAddress: line.trim(),
            key: uuid(),
          };
        });
        setParticipants(emails);
      }
    };
    reader.readAsText(inputFile);
  }

  async function handleListSave() {
    resetErrors();
    if (items.length < 2) {
      setCountItemsError(true);
      return;
    }
    if (items?.some((o) => o.name === '')) {
      setItemsNameError(true);
      return;
    }
    setIsSaving(true);
    try {
      await saveList(
        name,
        description,
        image,
        imageURL,
        items,
        type,
        participants
      );
      history.push(`/dashboard`);
      setIsSaving(false);
    } catch (e) {
      const eResp = e as Response;
      setShowErrorMessage(true);
      setErrorMessage(await eResp.text());
    }
  }
  function resetErrors() {
    setNameError(false);
    setEmptyParticipantsError(false);
    setEmptyParticipantEmailError(false);
    setItemsNameError(false);
    setCountItemsError(false);
  }
  function nextStep() {
    resetErrors();
    if (name === '') {
      setNameError(true);
      return;
    }
    if (type === 'closed' && (!participants || !participants.length)) {
      setEmptyParticipantsError(true);
      return;
    }
    if (participants?.some((o) => o.emailAddress === '')) {
      setEmptyParticipantEmailError(true);
      return;
    }
    setSaveDisabled(false);
    setAddRumblesSteps(2);
  }
  const saveButton = (
    <button
      type={'button'}
      className={clsx(
        'text-white px-4 py-2.5 flex justify-center items-center gap-2 rounded-6 bg-green font-semibold max-tablet:mt-4',
        (saveDisabled || isSaving) && 'opacity-50'
      )}
      disabled={saveDisabled || isSaving}
      onClick={handleListSave}
    >
      <SaveRumbleIcon />

      {isSaving ? 'Saving...' : 'Finish & Save Rumble'}
    </button>
  );
  return (
    <>
      <PageHeader
        props={props}
        title={'Create new Rumble'}
        logOutModal={props.logOutModal}
        editModal={props.editModal}
        isEditOpen={props.isEditOpen}
        isFullOpen={props.isFullOpen}
      >
      </PageHeader>
      <div
        className={
          'tablet:min-h-full container tablet:py-32 flex items-center justify-center'
        }
      >
        {!ready && !canCreateLists ? (
          <p className={'text-primary text-center'}>Loading...</p>
        ) : null}
        {ready && !canCreateLists ? (
          <div className={'text-center text-primary'}>
            <button
              type={'button'}
              onClick={() => props.videoModal(true)}
              className={
                'max-tablet:text-12 font-semibold text-blue tablet:mb-10 mb-5'
              }
            >
              How to use RumbleRank?
            </button>
            <div className={'flex justify-center'}>
              <ExceededRumbleIcon />
            </div>
            <div className={'tablet:mt-10 mt-5 flex flex-col gap-4'}>
              <div className={'text-base leading-155'}>
                <p>
                  You have exceeded the number of active rumble for{' '}
                  <span className={'text-blue font-semibold'}>
                    your account type.
                  </span>
                </p>
                <p>Go to you rumble management and delete some rumbles.</p>
              </div>
              <div className={'px-4 flex items-center gap-3'}>
                <hr className={'border-borderColor flex-1'} />
                <span className={'text-12 leading-1'}>OR</span>
                <hr className={'border-borderColor flex-1'} />
              </div>
              <p className={'text-base leading-155'}>
                Contact Jordan to rurchase an unlimited account
              </p>
              <button
                type={'button'}
                className={
                  'flex items-center text-white justify-center gap-2 w-full mb-3 h-10 px-3 py-2.5 font-semibold max-mobile:text-12 uppercase rounded-6 bg-gradient-to-r from-[#D13A0A] to-[#FFA901]'
                }
                onClick={() => props.upgradeModal(true)}
              >
                <GetUnlimiterIcon />
                Get Unlimited
              </button>
            </div>
          </div>
        ) : null}
        {ready && canCreateLists ? (
          <div
            className={
              'max-w-[689px] max-tablet:max-w-[335px] w-full flex flex-col gap-6 max-tablet:gap-4'
            }
          >
            <div className={'flex justify-between gap-2 max-tablet:flex-col'}>
              <h2 className={'h2 max-tablet:text-center'}>
                {addRumbleSteps === 1 ? 'General information' : 'Add list item'}
              </h2>
              <button
                type={'button'}
                onClick={() => props.videoModal(true)}
                className={'max-tablet:text-12 font-semibold text-blue'}
              >
                How to use RumbleRank?
              </button>
            </div>
            <div className={'flex flex-col gap-4'}>
              {addRumbleSteps === 1 && (
                <>
                  <div className={'flex gap-4 max-tablet:flex-col'}>
                    <div className={'tablet:basis-[281px]'}>
                      <Label
                        text={'Choose a primary image (recommended)'}
                        htmlFor={'rumbleImage'}
                      />
                      <div className={'relative h-[175px] max-tablet:h-40'}>
                        <CustomImageUpload
                          id={'rumbleImage'}
                          onChange={(e) => {
                            if (e.length) {
                              setImage(e[0]);
                            }
                          }}
                        />

                        {(image || imageURL) && (
                          <>
                            <button
                              className={
                                'h-6 w-6 flex items-center justify-center bg-primary/[0.75] rounded-full absolute top-2 right-2 z-10'
                              }
                              onClick={() => {
                                setImage(null);
                                setImageURL(null);
                              }}
                              title='Remove image'
                            >
                              <ClearFileInputIcon />
                            </button>
                            {image ? (
                              <img
                                className={
                                  'absolute w-full h-full top-0 left-0 rounded-8 object-cover'
                                }
                                src={URL.createObjectURL(image)}
                                alt={`Thumbnail for ${name || 'your new list'}`}
                              />
                            ) : (
                              imageURL && (
                                <img
                                  className={
                                    'absolute w-full h-full top-0 left-0 rounded-8 object-cover'
                                  }
                                  src={imageURL}
                                  alt={`Thumbnail for ${
                                    name || 'your new list'
                                  }`}
                                />
                              )
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    <div
                      className={
                        'flex flex-col gap-4 flex-grow max-tablet:order-first'
                      }
                    >
                      <div>
                        <Label
                          text={'Name your Rumble'}
                          htmlFor={'rumbleName'}
                          error={nameError}
                          errorText={'Enter Rumble Name'}
                        />
                        <Input
                          variant={'text'}
                          id={'rumbleName'}
                          value={name}
                          error={nameError}
                          placeholder={
                            'Give Your List a Title for Your Participants'
                          }
                          onChange={(e) => {
                            setNameError(false);
                            setName(e.target.value);
                          }}
                        />
                      </div>
                      <div>
                        <Label
                          text={'Describe your Rumble'}
                          htmlFor={'rumbleDescribe'}
                        />
                        <TextArea
                          id={'rumbleDescribe'}
                          placeholder={'Tell Participants How To Rank Items'}
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                  <div className={'flex flex-col gap-4'}>
                    <Label text={'Choose a Rumble type'} />
                    <Radio
                      id={'openRumble'}
                      name={'listTypeRadio'}
                      label={
                        'Sharing link (we generate a link for you to share)'
                      }
                      value={'open'}
                      checked={type === 'open'}
                      onChange={(e) => {
                        setType(e.target.value);
                        setEmptyParticipantsError(false);
                      }}
                    />
                    <Radio
                      id={'closeRumble'}
                      name={'listTypeRadio'}
                      label={
                        'Emailed link (we email a link to participants you submit)'
                      }
                      value={'closed'}
                      checked={type === 'closed'}
                      onChange={(e) => {
                        setType(e.target.value);
                        setEmptyParticipantsError(false);
                      }}
                    />
                    {type === 'closed' && (
                      <>
                        <CustomTextFileUpload
                          onChange={(e) => {
                            if (e.length) {
                              readParticipantList(e[0]);
                            }
                          }}
                        />
                        <div className={'flex items-center px-3 gap-3'}>
                          <hr className={'flex-1 border-borderColor'} />
                          <span className={'text-primary text-12 leading-1'}>
                            OR
                          </span>
                          <hr className={'flex-1 border-borderColor'} />
                        </div>
                        <div
                          className={
                            'bg-blueLight rounded-8 p-4 flex flex-col gap-4'
                          }
                        >
                          {participants
                            ? participants.map((p, index) => {
                                return (
                                  <div key={p.key}>
                                    <Label
                                      text={'Email Address:'}
                                      htmlFor={`participantEmail${index}`}
                                      errorText={
                                        'Enter participant email address'
                                      }
                                      error={
                                        emptyParticipantEmailError &&
                                        p.emailAddress === ''
                                      }
                                    />

                                    <div className={'flex gap-3'}>
                                      <Input
                                        variant={'text'}
                                        id={`participantEmail${index}`}
                                        value={p.emailAddress}
                                        onChange={(evt) => {
                                          updateParticipant({
                                            ...p,
                                            emailAddress: evt.target.value,
                                          });
                                        }}
                                      />

                                      <button
                                        onClick={() => {
                                          removeParticipant(p.key);
                                          setEmptyParticipantEmailError(false);
                                        }}
                                      >
                                        <DeleteIcon />
                                      </button>
                                    </div>
                                  </div>
                                );
                              })
                            : null}
                          <button
                            className={
                              'flex gap-2 text-blue text-base font-semibold'
                            }
                            onClick={() => {
                              addNewParticipant();
                              setEmptyParticipantsError(false);
                              setEmptyParticipantEmailError(false);
                            }}
                          >
                            <AddParticipantIcon />
                            Add participant manually
                          </button>
                        </div>
                        {emptyParticipantsError ? (
                          <p className={'text-red text-12 leading-1'}>
                            Add at least one participant
                          </p>
                        ) : null}
                      </>
                    )}
                  </div>
                </>
              )}

              {addRumbleSteps === 2 && (
                <>
                  {items.map((item, index) => {
                    return (
                      <Item
                        key={item.key}
                        item={item}
                        disableDelete={items.length === 1}
                        updateItem={updateItem}
                        removeItem={removeItem}
                        isMobile={props.isMobile}
                        index={index}
                        emptyName={itemsNameError}
                        resetEmptyName={() => setItemsNameError(false)}
                      />
                    );
                  })}
                  {countItemsError ? (
                    <p className={'text-12 text-red text-end'}>
                      Add one more list item
                    </p>
                  ) : null}
                </>
              )}
            </div>

            {addRumbleSteps === 1 && (
              <div
                className={
                  'flex justify-end max-tablet:flex-col gap-2 max-tablet:mt-4'
                }
              >
                <Button
                  variant={'primary'}
                  className={'tablet:w-[120px]'}
                  onClick={nextStep}
                >
                  Next
                </Button>
                {props.isMobile && saveButton}
              </div>
            )}
            {addRumbleSteps === 2 && (
              <>
                <div
                  className={'flex justify-between gap-2 max-tablet:flex-col'}
                >
                  <Button
                    variant={'secondary'}
                    onClick={() => {
                      setAddRumblesSteps(1);
                      resetErrors();
                    }}
                    className={'gap-2 max-tablet:border-blue'}
                  >
                    <BackArrowIcon />
                    Back
                  </Button>
                  <Button
                    variant={'primary'}
                    className={'tablet:w-[164px] gap-2'}
                    onClick={() => {
                      addNewItem();
                      resetErrors();
                    }}
                  >
                    <PlusIcon />
                    Add New List Item
                  </Button>
                  {props.isMobile && saveButton}
                </div>
              </>
            )}
            <div className="flex flex-col items-center">
              {items.length > 2 &&
                <p>{`In this rumble, participants will experience roughly ${Math.round(items.length * Math.log2(items.length))} matchups.`}</p>
              }
            </div>
            <div className="flex flex-col items-center">
              {saveButton}
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}
function mapStateToProps(state: any) {
  return {
    user: getUser(state),
    ready: isUserReady(state),
  };
}

export default connect(mapStateToProps, { setActiveList })(Create);
